/**
 * The View Transitions API, for the Insights page's motion (D58 of
 * documentation/pr-plans/insights-s0-plan-and-seats.md), only where the browser has it and the
 * reader allows motion. Everywhere else the change is simply made: a view's own motion-safe fade
 * (animate-fade-in) is its cross-fade, and a list's x-list-motion moves its rows with auto-animate.
 */
export function viewTransitionsAllowed() {
    return (
        typeof document.startViewTransition === "function" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
}

/**
 * Grows `from` into `to` while `update` changes the DOM, as one view transition: the two share a
 * view-transition-name for the transition only, `from` in the old state and `to` in the new, so the
 * browser animates the one box into the other (misc.css times it on the `view` token).
 *
 * @param {HTMLElement|null} from what was pressed, visible before the change
 * @param {HTMLElement|null} to what it opens, visible after the change
 * @param {() => (void|Promise<void>)} update makes the change
 * @returns {Promise<void>} settles once the DOM shows the change
 */
export function growInto(from, to, update, name = "insights-grow") {
    if (!viewTransitionsAllowed() || !from || !to) {
        return Promise.resolve(update());
    }

    from.style.viewTransitionName = name;
    let overlays = nameOverlays();

    const transition = document.startViewTransition(async () => {
        from.style.viewTransitionName = "";
        unname(overlays);
        to.style.viewTransitionName = name;
        await update();
        overlays = nameOverlays();
    });

    transition.finished.finally(() => {
        to.style.viewTransitionName = "";
        unname(overlays);
    });

    // Settles when `update` does, even for a transition the browser skips (another started, the page
    // hid), and rejects only when `update` itself threw, which is left to surface.
    return transition.updateCallbackDone;
}

// The lists x-list-motion marks (insights/list-motion.js), each with an id of its own, so a row's
// view-transition-name is the same before a change and after it.
const lists = new Map();
let listCount = 0;

/** @returns {() => void} forgets the list */
export function registerList(el) {
    lists.set(el, ++listCount);

    return () => lists.delete(el);
}

/**
 * Names every row of every marked list for one transition, so each travels on its own: a row's key is
 * its data-motion-key, its wire:key, its data-row-id or its id, and a row without one, or with a key
 * another row of its list already took, moves with the page instead.
 *
 * @returns {HTMLElement[]} the rows named
 */
function nameRows() {
    const named = [];

    for (const [list, id] of lists) {
        if (!list.isConnected) {
            continue;
        }

        const taken = new Set();

        for (const row of list.children) {
            const key = row.getAttribute("data-motion-key") ?? row.getAttribute("wire:key") ?? row.getAttribute("data-row-id") ?? row.id;
            const name = key ? `insights-row-${id}-${key.replace(/[^a-zA-Z0-9_-]/g, "-")}` : "";

            if (name === "" || taken.has(name)) {
                continue;
            }

            taken.add(name);
            row.style.viewTransitionName = name;
            named.push(row);
        }
    }

    return named;
}

function unname(rows) {
    rows.forEach((row) => {
        row.style.viewTransitionName = "";
    });
}

/**
 * Names every open overlay for one transition: the drawers, dialogs, tour and toasts mark their fixed
 * layer data-view-transition-overlay. A transition paints its named parts in a layer over the whole
 * page, in the page's paint order, above one picture of everything left unnamed, so a row named while
 * the drawer over it was not slid over the drawer and its backdrop for the transition's length (Ola's
 * QA, 9 Oct: Undo in Draft an article put its row back over the drawer). Named too, an open overlay is
 * a part of its own, painted after the rows it covers, as it is outside a transition. A hidden overlay
 * draws no box and is left unnamed.
 *
 * @returns {HTMLElement[]} the overlays named
 */
function nameOverlays() {
    const named = [];

    document.querySelectorAll("[data-view-transition-overlay]").forEach((overlay, at) => {
        if (overlay.getClientRects().length === 0) {
            return;
        }

        overlay.style.viewTransitionName = `insights-overlay-${at}`;
        named.push(overlay);
    });

    return named;
}

// Holds on list motion (holdListMotion()) not yet let go, and the moves asked for meanwhile.
let holds = 0;
let waiting = [];

/**
 * Holds every list move ($moveRows) until the function it returns is called, for motion that a view transition's
 * frozen picture must not catch: Copy prompt's pill flies from its row as the row folds (P101, D217), and a fold's
 * transition would hold the pill still for its length, then let it jump, or ghost it in Firefox. Moves asked for
 * meanwhile wait, then move as they always do, in the order asked. Calling it twice lets go once.
 *
 * @returns {() => void} lets go
 */
export function holdListMotion() {
    let isHeld = true;

    holds++;

    return () => {
        if (!isHeld) {
            return;
        }

        isHeld = false;
        holds--;

        if (holds === 0) {
            const moves = waiting;

            waiting = [];
            moves.forEach((move) => move());
        }
    };
}

/**
 * Re-ranks or thins a list as one view transition, so rows slide to their new places and the rows
 * that leave fade out (D58: Done, Not relevant, a stage change, a move, a regroup), on the `morph`
 * token. The rows of each list marked x-list-motion are named for the transition and unnamed after it.
 * `update` must change the list's DOM at once, or with a promise that settles within a frame or two:
 * the browser holds the old picture on screen until it settles, so a network round trip belongs
 * before or after it, never inside. Where there is no View Transitions API the change is just made,
 * and the list's x-list-motion moves its rows with auto-animate; under reduced motion neither runs.
 *
 * @param {() => (void|Promise<void>)} update changes the list
 * @returns {Promise<void>} settles once the DOM shows the change
 */
export function moveRows(update) {
    if (!viewTransitionsAllowed()) {
        return Promise.resolve(update());
    }

    if (holds > 0) {
        return new Promise((resolve, reject) => {
            waiting.push(() => moveRows(update).then(resolve, reject));
        });
    }

    const root = document.documentElement;
    let named = [...nameRows(), ...nameOverlays()];
    root.dataset.listMotion = "";

    const transition = document.startViewTransition(async () => {
        await update();
        unname(named);
        named = [...nameRows(), ...nameOverlays()];
    });

    transition.finished.finally(() => {
        unname(named);
        delete root.dataset.listMotion;
    });

    return transition.updateCallbackDone;
}
